import { useQuery, useQueries } from "@tanstack/react-query";
import lodash from "lodash";

import type { ICategory, ICustomCategory } from "app/common/services/categoryService.types";
import { DefaultFetchService } from "app/services/fetchService/fetchService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { ICategoryComparison } from "../../categories-context";
import { extractCategoriesFromRouteParams, extractPrimaryCategoryFromParams } from "../../routes/routeUtils";
import type { GeographyTableResponse, GeographyGraphResponse, GeographyTableData } from "../types";
import { getApiParams, transformTableData } from "../utils/utils";

// API endpoint constants
const API_ENDPOINTS = {
    graph: "api/geographyCategory/graph",
    table: "api/geographyCategory/table",
} as const;

// Query key prefixes for better query cache organization
const QUERY_KEYS = {
    geographyGraph: "geography-graph",
    geographyTable: "geography-table",
} as const;

// Common API query parameters for geography endpoints
const API_QUERY_PARAMS = ["webSource", "keys", "from", "to", "isWindow", "includeSubdomains"] as const;

/**
 * Helper function to prepare API query parameters
 */
const prepareApiQueryParams = () => {
    const apiParams = getApiParams();
    return lodash.pick(apiParams, API_QUERY_PARAMS);
};

/**
 * Helper function to fetch geography table data for a specific category
 */
const fetchGeographyTableData = async (
    category: ICategory | ICustomCategory,
): Promise<GeographyTableResponse> => {
    const fetchService = DefaultFetchService.getInstance();
    const baseQueryParams = getApiParams();

    return fetchService.get<GeographyTableResponse>(API_ENDPOINTS.table, {
        ...baseQueryParams,
        keys: category.forApi,
        categories: category.forApi,
        categoryHash: category.categoryHash,
    });
};

/**
 * Hook to fetch geography graph data for chart visualization
 */
export function useGeographyGraphData() {
    const params = proNavigator.getParams();
    const categories = extractCategoriesFromRouteParams(params);
    const category = extractPrimaryCategoryFromParams(categories);

    return useQuery<GeographyGraphResponse>({
        queryKey: [QUERY_KEYS.geographyGraph, category, params.duration, params.comparedDuration],
        queryFn: async () => {
            const apiQueryParams = prepareApiQueryParams();
            const fetchService = DefaultFetchService.getInstance();
            const response = await fetchService.get(API_ENDPOINTS.graph, apiQueryParams);

            return (response || {}) as GeographyGraphResponse;
        },
    });
}

/**
 * Hook to fetch geography table data for data table display
 */
export function useGeographyTableSingleData(queryOptions?: { enabled?: boolean }) {
    const params = proNavigator.getParams();
    const categories = extractCategoriesFromRouteParams(params);
    const category = extractPrimaryCategoryFromParams(categories);

    return useQuery<GeographyTableData, unknown, GeographyTableData>({
        queryKey: [QUERY_KEYS.geographyTable, category, params.duration, params.comparedDuration],
        queryFn: async () => {
            const apiQueryParams = prepareApiQueryParams();
            const fetchService = DefaultFetchService.getInstance();
            const response = await fetchService.get(API_ENDPOINTS.table, apiQueryParams);
            const rawData =
                response && (response as GeographyTableResponse).Data ? (response as GeographyTableResponse) : null;

            return transformTableData(rawData);
        },
        enabled: queryOptions?.enabled ?? true,
    });
}

/**
 * Hook to fetch geography comparison data for multiple categories
 */
export function useGeographyComparisonData(categories: ICategoryComparison[], queryOptions?: { enabled?: boolean }) {
    const { duration, comparedDuration } = proNavigator.getParams();

    const queries = categories.map((categoryComparison) => ({
        queryKey: [QUERY_KEYS.geographyTable, "comparison", categoryComparison.id, duration, comparedDuration],
        queryFn: () => fetchGeographyTableData(categoryComparison.category),
        select: (data: GeographyTableResponse) => transformTableData(data),
        enabled: queryOptions?.enabled ?? categories.length > 0,
    }));

    const results = useQueries({ queries });

    return {
        data: results.map((r) => r.data),
        isLoading: results.some((r) => r.isLoading),
        isError: results.some((r) => r.isError),
        errors: results.map((r) => r.error).filter(Boolean),
    };
}
